'use client';

import { ArrowRight } from 'lucide-react';

import { useState, useCallback, useEffect } from 'react';

import trustedByLogos from '@/assets/trusted-by-logos.png';
import { AnalysisPreview } from '@/components/analyze/analysis-preview';
import { AnalyzingAnimation } from '@/components/analyze/analyzing-animation';
import { FileUploader } from '@/components/analyze/file-uploader';
import { FullReport } from '@/components/analyze/full-report';
import { GradeReveal } from '@/components/analyze/grade-reveal';
import { LeadCaptureForm, type Lead } from '@/components/analyze/lead-capture-form';
import { MarketingStats } from '@/components/analyze/marketing-stats';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ensureHubSpotTracking, trackFunnelEvent } from '@/lib/analytics';
import { scoreProject, type DCMAScoreResult } from '@/lib/dcma-scoring-engine';
import { DEFAULT_HUBSPOT_FORM_ID, DEFAULT_HUBSPOT_PORTAL_ID } from '@/lib/hubspot-config';
import { parseMSPXML, isMSProjectXML } from '@/lib/msp-parser';
import { parseP6XML } from '@/lib/p6-parser';

export type WidgetProps = {
  /**
   * HubSpot portal ID. Defaults to DEFAULT_HUBSPOT_PORTAL_ID when omitted; pass
   * an empty string to disable the HubSpot submission.
   */
  hubspotPortalId?: string;
  /**
   * HubSpot form GUID. Defaults to DEFAULT_HUBSPOT_FORM_ID when omitted; pass an
   * empty string to disable the HubSpot submission.
   */
  hubspotFormId?: string;
};

type AnalysisState =
  | { stage: 'upload' }
  | { stage: 'analyzing'; pendingResult?: DCMAScoreResult; pendingFileName?: string }
  | { stage: 'results'; result: DCMAScoreResult; fileName: string }
  | { stage: 'error'; error: string };

export const Widget = ({
  hubspotPortalId = DEFAULT_HUBSPOT_PORTAL_ID,
  hubspotFormId = DEFAULT_HUBSPOT_FORM_ID,
}: WidgetProps) => {
  const [state, setState] = useState<AnalysisState>({ stage: 'upload' });
  const [lead, setLead] = useState<Lead | null>(null);

  // Make funnel instrumentation self-sufficient by loading the HubSpot tracking
  // code for the configured portal (no-op when HubSpot is disabled).
  useEffect(() => {
    if (hubspotPortalId) {
      ensureHubSpotTracking(hubspotPortalId);
    }
  }, [hubspotPortalId]);

  const handleFileSelected = useCallback(async (file: File) => {
    setState({ stage: 'analyzing' });
    setLead(null);
    trackFunnelEvent('dcma_file_uploaded');
    try {
      const fileText = await file.text();

      const project = isMSProjectXML(fileText) ? parseMSPXML(fileText) : parseP6XML(fileText);

      if (project.activities.length === 0) {
        throw new Error(
          'No activities found in the uploaded file. Please ensure this is a valid P6 XML or Microsoft Project XML export.',
        );
      }
      const result = scoreProject(project);
      trackFunnelEvent('dcma_parse_complete', { grade: result.grade, score: result.totalScore });
      // Store result but keep in "analyzing" stage until the animation completes.
      setState({ stage: 'analyzing', pendingResult: result, pendingFileName: file.name });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unknown error occurred while analyzing the file.';
      trackFunnelEvent('dcma_parse_error', { message });
      setState({ stage: 'error', error: message });
    }
  }, []);

  const handleAnimationComplete = useCallback(() => {
    setState(prev => {
      if (prev.stage !== 'analyzing' || !prev.pendingResult || !prev.pendingFileName) {
        return prev;
      }
      // Track at the transition that promotes analysis into visible results.
      trackFunnelEvent('dcma_results_viewed', {
        grade: prev.pendingResult.grade,
        score: prev.pendingResult.totalScore,
      });
      return { stage: 'results', result: prev.pendingResult, fileName: prev.pendingFileName };
    });
  }, []);

  const handleLeadUnlock = useCallback(
    (leadData: Lead) => {
      setLead(leadData);
      if (state.stage === 'results') {
        // Track at the transition where the submitted form unlocks the full report.
        trackFunnelEvent('dcma_full_results_viewed', {
          grade: state.result.grade,
          score: state.result.totalScore,
        });
      }
    },
    [state],
  );

  const handleReset = useCallback(() => {
    setLead(null);
    setState({ stage: 'upload' });
  }, []);

  return (
    <div className="wfw-root min-h-screen bg-background">
      <main className="mx-auto max-w-6xl px-6">
        {/* Upload state: hero + uploader */}
        {state.stage === 'upload' && (
          <div className="py-16 sm:py-24">
            {/* Hero */}
            <div className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-4 py-1.5 ring-1 ring-brand-orange/30">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
                <p className="text-xs font-bold uppercase tracking-widest text-brand-orange">
                  Free DCMA 14-Point Assessment
                </p>
              </div>
              <h1 className="mt-5 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl text-balance leading-[1.1]">
                Know your schedule quality in <span className="text-brand-orange">seconds</span>
              </h1>
              <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground leading-relaxed text-pretty">
                Drop in your P6 or Microsoft Project schedule and get an instant, industry-standard quality grade.
              </p>
              <p className="mx-auto max-w-xl text-lg text-muted-foreground leading-relaxed text-pretty">
                No sign-up. No waiting. Completely free.
              </p>
            </div>

            {/* Uploader */}
            <div className="mt-12">
              <FileUploader onFileSelected={file => void handleFileSelected(file)} />
            </div>

            {/* Social proof */}
            <div className="mt-16 text-center">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
                Trusted by 100+ modern construction teams
              </p>
              <img
                src={trustedByLogos}
                alt="Logos of construction teams that trust Planera, including Big-D Construction, Barton Malow, Stacy Witbeck, Garney, Balfour Beatty, Swinerton, HITT, Ryan, Hensel Phelps, Aldridge, Butz Family of Companies, Harkins, Okland Construction, and Zachry Construction"
                className="mx-auto mt-8 w-full max-w-3xl opacity-80"
              />
            </div>

            {/* Value props */}
            <div className="mt-12 border-t border-border pt-12">
              <div className="grid gap-6 sm:grid-cols-3">
                {[
                  {
                    title: 'Complete DCMA Assessment',
                    desc: 'All 14 criteria evaluated: logic quality, relationships, float, constraints, durations, critical path, and more.',
                    color: 'brand-lime',
                  },
                  {
                    title: 'Actionable Intelligence',
                    desc: 'Get a letter grade, executive summary, and specific recommendations to strengthen your schedule before it costs you.',
                    color: 'brand-orange',
                  },
                  {
                    title: 'Private & Secure',
                    desc: 'Your schedule never leaves your browser. Analysis runs entirely client-side. Nothing is uploaded to any server.',
                    color: 'brand-navy',
                  },
                ].map(item => (
                  <div
                    key={item.title}
                    className="group rounded-lg border border-border p-6 text-center sm:text-left hover:border-current transition-colors"
                    style={{ borderColor: `var(--${item.color})` }}
                  >
                    <div
                      className="mb-3 inline-flex h-2 w-12 rounded-full"
                      style={{ backgroundColor: `var(--${item.color})` }}
                    />
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="relative mt-16 overflow-hidden rounded-xl border border-brand-orange/30 bg-brand-orange p-8 text-center sm:p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/90 via-transparent to-brand-orange/70" />
              <div className="relative">
                <h2 className="text-lg font-semibold text-white sm:text-xl text-balance">
                  Building better schedules starts with knowing where you stand
                </h2>
                <p className="mx-auto mt-3 max-w-lg text-sm text-white leading-relaxed">
                  Planera helps construction teams create, manage, and improve schedules on a single collaborative
                  platform. Run your free quality check above, then see how Planera can help you fix what it finds.
                </p>
                <a
                  href="https://www.planera.io?utm_source=dcma-checker&utm_medium=cta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-brand-orange shadow-lg shadow-black/10 transition-all hover:bg-white/90 hover:shadow-xl"
                >
                  Learn more about Planera
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Analyzing state: animation */}
        {state.stage === 'analyzing' && <AnalyzingAnimation onComplete={handleAnimationComplete} />}

        {/* Error state */}
        {state.stage === 'error' && (
          <div className="flex flex-col items-center justify-center gap-6 py-32">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <svg
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <div className="text-center">
              <h2 className="text-lg font-semibold text-foreground">Analysis Failed</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">{state.error}</p>
            </div>
            <Button onClick={handleReset} className="mt-2 gap-2">
              Try Again
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Results state: grade + lead capture + (once unlocked) full report */}
        {state.stage === 'results' && (
          <div className="py-10">
            <div className="mx-auto max-w-2xl flex flex-col gap-10">
              {/* Section 1: Letter score (grade reveal) */}
              <GradeReveal
                grade={state.result.grade}
                totalScore={state.result.totalScore}
                projectName={state.result.projectName}
              />

              <Separator className="my-2" />

              {/* Section 2: Analysis preview (Planera headline + subtext) */}
              <AnalysisPreview grade={state.result.grade} />

              {/* Section 3: Form fill (lead capture) */}
              <LeadCaptureForm
                grade={state.result.grade}
                projectName={state.result.projectName}
                scoreResult={state.result}
                hubspotPortalId={hubspotPortalId}
                hubspotFormId={hubspotFormId}
                onUnlock={handleLeadUnlock}
              />

              {/* Section 4: full report, revealed once the form is submitted */}
              {lead && (
                <>
                  <Separator className="my-2" />
                  <FullReport result={state.result} lead={lead} />
                </>
              )}

              <Separator className="my-2" />

              {/* Section 5: Marketing blurb (social proof stats) */}
              <MarketingStats />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
